import { GoogleAuthProvider, signInWithPopup, getAuth, createUserWithEmailAndPassword } from "firebase/auth";
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { FaGoogle } from "react-icons/fa";
import { useRedirectIfLoggedInHook } from './useRedirectIfLoggedInHook';
import {
    Box,
    Button,
    Center,
    Divider,
    FormControl,
    FormLabel,
    Input,
    Link,
    FormHelperText,
    SimpleGrid,
    Heading,
    Container,
    Stepper,
    Step,
    StepIcon,
    StepSeparator,
    StepStatus,
    StepNumber,
    StepIndicator,
    Flex,
    Text,
    Switch,
    Spinner
} from '@chakra-ui/react';
import { SearchIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet-async';
import { secureFetch } from '../data/secureFetch';
const { capitalizeWords } = require('../store/calc-helpers');

function Register() {
    const [activeStep, setActiveStep] = useState(0); // Starts on Step 1
    const [formData, setFormData] = useState({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        organizationName: '',
    });
    const [errors, setErrors] = useState({});
    const [success, setSuccess] = useState(false);
    const [attemptedSearch, setAttemptedSearch] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isEinSearch, setIsEinSearch] = useState(false);
    const [einInput, setEinInput] = useState('');
    const [selectedOrganization, setSelectedOrganization] = useState(null);
    const [loading, setLoading] = useState(false); // For loading state during search
    const [submitLoading, setSubmitLoading] = useState(false); // For loading state during search
    const [emailVerified, setEmailVerified] = useState(false);

    useRedirectIfLoggedInHook();

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const validateFieldNotEmpty = () => {
        let isValidFirstName = true;
        if (!formData.firstName) {
            isValidFirstName = false;
        }
        setErrors(prev => ({ ...prev, firstName: isValidFirstName ? '' : 'First Name Required' }));

        return isValidFirstName;
    };

    const validateEmail = () => {
        const isValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
        setErrors(prev => ({ ...prev, email: isValid ? '' : 'Invalid email address' }));
        return isValid;
    };

    const validatePassword = () => {
        if (formData.googleUid) return true; // Skip validation for Google registration
        const isValid = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[!@#$%^&*]).{8,}$/.test(formData.password);
        setErrors(prev => ({ ...prev, password: isValid ? '' : 'Password must be at least 8 characters long and include uppercase, lowercase, number, and special character' }));
        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Ensure the organization is selected
        if (!selectedOrganization) {
            setErrors(prev => ({ ...prev, organization: "Please select an organization" }));
            return;
        }

        try {
            setSubmitLoading(true);

            const auth = getAuth();
            // If Google was used for registration, or use Firebase for email/password signup
            const userCredential = formData.googleUid
                ? { user: { uid: formData.googleUid, getIdToken: () => auth.currentUser.getIdToken() } }
                : await createUserWithEmailAndPassword(auth, formData.email, formData.password);

            const { uid } = userCredential.user;

            // Get Firebase ID token for server-side validation
            const idToken = await userCredential.user.getIdToken();

            // Send the user data, organization info, uid, and idToken to the backend
            const response = await secureFetch(`/api/auth/register`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                data: {
                    email: formData.email,
                    firstName: formData.firstName,
                    lastName: formData.lastName,
                    organizationName: capitalizeWords(selectedOrganization.name),
                    organizationEin: selectedOrganization.ein,
                    uid,
                    idToken, // Now passing the idToken to backend
                    isGoogle: !!formData.googleUid
                },
            });

            if (!response.ok) {
                const errorData = response;
                throw new Error(errorData.message);
            }

            if (response.ok) {
                const data = response;
                setEmailVerified(data.emailVerified);
            }

            setSuccess(true);
        } catch (error) {
            console.error("Registration error:", error);
            setErrorMessage(error.message);
        } finally {
            setSubmitLoading(false);
        }
    };

    // Inside your Register component
    const handleGoogleSignIn = async () => {
        const auth = getAuth();
        const provider = new GoogleAuthProvider();

        try {
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            // Store the Google user's name and email in form data
            setFormData(prev => ({
                ...prev,
                email: user.email,
                firstName: user.displayName.split(" ")[0],
                lastName: user.displayName.split(" ")[1] || "",
                googleUid: user.uid,  // Storing Google UID for later registration
            }));

            // Optionally move to Step 2 directly after successful Google sign-in
            setActiveStep(1);
        } catch (error) {
            console.error("Google Sign-In Error:", error);
            setErrorMessage(error.message);
        }
    };

    // Handle switch between EIN and Organization name search
    const handleEinOrOrgSwitch = () => {
        setIsEinSearch((prev) => !prev);
        setAttemptedSearch(false);
        setSearchResults([]); // Clear search results when switching
        setSelectedOrganization(null); // Clear selected organization
    };

    // Helper function to format the EIN with a dash after the first two digits
    const handleEinChange = (e) => {
        let value = e.target.value.replace(/\D/g, ''); // Remove non-numeric characters
        if (value.length > 2) {
            value = `${value.slice(0, 2)}-${value.slice(2, 9)}`; // Add dash after the first two digits
        }
        setEinInput(value);
    };

    // Validation for EIN format before searching
    const handleEinSearch = async () => {
        setAttemptedSearch(true);
        const ein = einInput.replace('-', '');  // Remove dashes from EIN input
        const charityData = await searchCharity(ein, 'ein');

        if (charityData.data) {
            setSearchResults([charityData.data]);  // Assuming response contains charity data
        } else {
            setSearchResults([]);  // Clear or handle empty results
        }
    };

    const searchCharity = async (query, type) => {
        try {
            setLoading(true);
            const response = await secureFetch(`/api/charity/search?query=${query}&type=${type}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (!response.ok) {
                throw new Error('Failed to fetch charity data');
            }

            const data = response;
            setLoading(false);
            return data;  // Process the data as needed
        } catch (error) {
            setLoading(false);
            console.error('Error searching charity:', error);
            return null;  // Handle errors appropriately
        }
    };

    // Handle organization name search
    const handleOrgSearch = async () => {
        setAttemptedSearch(true);
        const charityData = await searchCharity(formData.organizationName, 'org');

        if (charityData) {
            setSearchResults(charityData.data.slice(0, 5));  // Limit to 5 results
        } else {
            setSearchResults([]);  // Clear or handle empty results
        }
    };

    // Handle selecting an organization
    const handleSelectOrg = (org) => {
        setSelectedOrganization(org);
    };

    const renderSearchResults = () => {
        if (loading) {
            return <Spinner />;
        }

        if (searchResults.length === 0
            && attemptedSearch) {
            return <Text>No results found.</Text>;
        }

        return searchResults.map((org, idx) => (
            <Box
                key={idx}
                p={4}
                mb={4}
                borderWidth="1px"
                borderRadius="lg"
                onClick={() => handleSelectOrg(org)}
                cursor="pointer"
                _hover={{ bg: 'gray.50' }}
                bg={selectedOrganization?.ein === org.ein ? 'orange.100' : 'white'}
                border={selectedOrganization?.ein === org.ein ? '2px solid orange' : '1px solid gray'}
            >
                <Text fontWeight="bold">{org.name}</Text>
                <Text>EIN: {org.ein.slice(0, 2)}-{org.ein.slice(2)}</Text>
                <Text>{org.city}, {org.state}</Text>
            </Box>
        ));
    };

    const renderSelectedOrganization = () => {
        if (!selectedOrganization) return null;
        return (
            <Box p={4} mb={4} borderWidth="1px" borderRadius="lg" bg="gray.100">
                <Text fontWeight="bold">{selectedOrganization.name}</Text>
                <Text>EIN: {selectedOrganization.ein.slice(0, 2)}-{selectedOrganization.ein.slice(2)}</Text>
                <Text>{selectedOrganization.city}, {selectedOrganization.state}</Text>
                <Text onClick={() => setSelectedOrganization(null)} color="blue.500" cursor="pointer">Go Back</Text>
            </Box>
        );
    };

    if (submitLoading) {
        return <Center><Spinner size="xl" color="orange.500" /></Center>;
    }

    if (success) {
        return (
            <Container maxW="lg" py={12} px={6} minH="60vh">
                <Box rounded="lg" boxShadow="lg" p={8} bg="white" textAlign="center">
                    <Heading as="h2" size="xl" mb={6}>
                        Registration Successful
                    </Heading>
                    {emailVerified ? (
                        <p>Your email is verified. You can now <Link as={RouterLink} to="/login" color="blue.500">log in</Link>.</p>
                    ) : (
                        <p>Please check your email for a verification link.</p>
                    )}
                </Box>
            </Container>
        );
    }

    const campaignImage = `https://${window.location.host}/images/share-image.png`;

    return (
        <Container maxW="lg" py={12} px={6} minH="60vh">
            <Box
                rounded="lg"
                boxShadow="lg"
                p={8}
                bg="white"
            >
                <Helmet>
                    <title>Register - PurelyGive</title>
                    <meta name="description" content="Create your PurelyGive account and start raising funds for your nonprofit without any transaction fees. Join our zero-fee fundraising platform today!" />
                    <meta name="keywords" content="register, create account, fundraising platform, nonprofit registration, PurelyGive" />
                    <meta property="og:title" content="Register on PurelyGive - Start Raising Funds" />
                    <meta property="og:description" content="Sign up for PurelyGive and raise funds for your nonprofit without paying transaction fees. Join the zero-fee fundraising revolution today!" />
                    <meta property="og:image" content={campaignImage} />
                    <meta property="og:url" content="https://purelygive.com/register" />
                </Helmet>
                <Heading as="h2" size="xl" mb={6} textAlign="center">Register</Heading>
                {errorMessage && (
                    <Box mb={4} color="red.500" textAlign="center">
                        {errorMessage}
                    </Box>
                )}

                {activeStep !== 0 && (
                    <Stepper index={activeStep} colorScheme="orange">
                        <Step>
                            <StepIndicator>
                                <StepStatus
                                    complete={<StepIcon />}
                                    incomplete={<StepNumber />}
                                    active={<StepNumber />}
                                />
                            </StepIndicator>
                            <StepSeparator />
                        </Step>

                        <Step>
                            <StepIndicator>
                                <StepStatus
                                    complete={<StepIcon />}
                                    incomplete={<StepNumber />}
                                    active={<StepNumber />}
                                />
                            </StepIndicator>
                            <StepSeparator />
                        </Step>
                    </Stepper>
                )}

                {/* Form Content */}
                <form onSubmit={handleSubmit}>

                    {activeStep === 0 && (
                        <Box>
                            {/* Google Login Button with Icon */}
                            <Button
                                mt={4}
                                colorScheme="blue"
                                leftIcon={<FaGoogle />}
                                w="full"
                                onClick={handleGoogleSignIn}
                            >
                                Register with Google
                            </Button>

                            <Box display="flex" alignItems="center" mt={6} mb={6}>
                                <Divider />
                                <Text
                                    as="span"
                                    px={2}
                                    color="gray.500"
                                    fontWeight="bold"
                                    fontSize="sm"
                                >
                                    OR
                                </Text>
                                <Divider />
                            </Box>

                            {/* Form for First Name, Last Name, Email */}
                            <SimpleGrid columns={2} spacing={4}>
                                <FormControl isRequired isInvalid={!!errors.firstName}>
                                    <FormLabel htmlFor='firstName'>First Name</FormLabel>
                                    <Input
                                        id='firstName'
                                        name='firstName'
                                        value={formData.firstName}
                                        onChange={handleChange}
                                        onBlur={validateFieldNotEmpty}
                                    />
                                    <FormHelperText>{errors.firstName}</FormHelperText>
                                </FormControl>
                                <FormControl isInvalid={!!errors.lastName}>
                                    <FormLabel htmlFor='lastName'>Last Name</FormLabel>
                                    <Input
                                        id='lastName'
                                        name='lastName'
                                        value={formData.lastName}
                                        onChange={handleChange}
                                    />
                                    <FormHelperText>{errors.lastName}</FormHelperText>
                                </FormControl>
                            </SimpleGrid>

                            <SimpleGrid columns={1} spacing={4} mt={4}>
                                <FormControl isRequired isInvalid={!!errors.email}>
                                    <FormLabel htmlFor='email'>Email</FormLabel>
                                    <Input
                                        id='email'
                                        name='email'
                                        type='email'
                                        value={formData.email}
                                        onChange={handleChange}
                                        onBlur={validateEmail}
                                    />
                                    <FormHelperText>{errors.email}</FormHelperText>
                                </FormControl>
                            </SimpleGrid>

                            {!formData.googleUid && (
                                <FormControl isRequired isInvalid={!!errors.password}>
                                    <FormLabel htmlFor='password'>Password</FormLabel>
                                    <Input
                                        id='password'
                                        name='password'
                                        type='password'
                                        value={formData.password}
                                        onChange={handleChange}
                                        onBlur={validatePassword}
                                    />
                                    <FormHelperText>{errors.password}</FormHelperText>
                                </FormControl>
                            )}

                            {/* Continue with Email Button */}
                            <Button
                                mt={4}
                                colorScheme="orange"
                                type="button"
                                w="full"
                                onClick={() => setActiveStep(1)}
                            >
                                Register with Email
                            </Button>
                        </Box>
                    )}

                    {activeStep === 1 && (
                        <>
                            <FormControl display="flex" alignItems="center" mt={4}>
                                <FormLabel htmlFor="switch" mb="0">
                                    Search by EIN
                                </FormLabel>
                                <Switch id="switch" onChange={handleEinOrOrgSwitch} isChecked={isEinSearch} />
                            </FormControl>

                            {isEinSearch ? (
                                <FormControl isRequired isInvalid={!!errors.ein}>
                                    <FormLabel htmlFor="ein">EIN</FormLabel>
                                    <Input
                                        id="ein"
                                        name="ein"
                                        value={einInput}
                                        onChange={handleEinChange} // Apply EIN formatting
                                        maxLength={10} // Limit to 9 digits + 1 dash
                                        placeholder="XX-XXXXXXX"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault(); // Prevent form submission
                                                handleEinSearch(); // Trigger EIN search
                                            }
                                        }}
                                    />
                                    {errors.ein && <FormHelperText color="red.500">{errors.ein}</FormHelperText>}
                                    <Button onClick={handleEinSearch} colorScheme="orange" mt={2} mb={4}>
                                        Search by EIN
                                    </Button>
                                </FormControl>
                            ) : (
                                <FormControl isRequired>
                                    <FormLabel htmlFor="organizationName">Organization Name</FormLabel>
                                    <Input
                                        id="organizationName"
                                        name="organizationName"
                                        value={formData.organizationName}
                                        onChange={handleChange}
                                        placeholder="Enter Organization Name"
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault(); // Prevent form submission
                                                handleOrgSearch(); // Trigger Organization Name search
                                            }
                                        }}
                                    />
                                    {errors.org && <FormHelperText color="yellow.500">{errors.org}</FormHelperText>}
                                    <Button onClick={handleOrgSearch} colorScheme="orange" mt={2} mb={4}>
                                        <SearchIcon mr={2} />
                                        Search Organization
                                    </Button>
                                </FormControl>
                            )}

                            {selectedOrganization ? (
                                renderSelectedOrganization()
                            ) : (
                                renderSearchResults()
                            )}

                            <Flex mt={4} justifyContent="space-between">
                                <Button
                                    leftIcon={<ArrowBackIcon />}
                                    colorScheme="gray"
                                    variant="outline"
                                    onClick={() => setActiveStep(0)}
                                >
                                    Back
                                </Button>
                                <Button
                                    colorScheme="orange"
                                    type="submit"
                                    isDisabled={!selectedOrganization}
                                >
                                    Complete Registration
                                </Button>
                            </Flex>
                        </>
                    )}
                </form>
            </Box>
            <Text p={5} textAlign="center">
                Already have an account?{' '}
                <Link as={RouterLink} to="/login" color="blue.500">
                    Click here to login!
                </Link>
            </Text>
        </Container>
    );
}

export default Register;
